import { IconButton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import clsx from 'clsx';
import { getWeek, setDefaultOptions, startOfWeek, subWeeks } from 'date-fns';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import isValid from 'date-fns/isValid';
import isWithinInterval from 'date-fns/isWithinInterval';
import { enUS } from 'date-fns/locale';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useStyles from '../../../shipping-line/components/update-expiry-date/styles';

type Props = {
  fieldName: string;
};

const today = new Date();
const twoWeeksAgo = subWeeks(today, 2);

const WeekPicker = ({ fieldName }: Props) => {
  const { t } = useTranslation('common');
  const { setFieldValue } = useFormikContext();
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const { classes } = useStyles();

  const handleWeekChange = (date: Date | null) => {
    if (date) {
      setFieldValue(fieldName, {
        startDate: startOfWeek(date, { weekStartsOn: 1 }),
        endDate: endOfWeek(date),
      });
      setSelectedDate(startOfWeek(date, { weekStartsOn: 1 }));
    }
  };

  const formatWeekSelectLabel = (date: Date | null) => {
    if (date && isValid(date)) {
      const weekNumber = getWeek(date);
      const startDate = format(startOfWeek(date, { weekStartsOn: 1 }), 'dd MMM');
      const endDate = format(endOfWeek(date, { weekStartsOn: 1 }), 'dd MMM');

      return t('dates.selectedWeek', { weekNumber, startDate, endDate });
    }

    return '';
  };

  const renderWeekPickerDay = (props: PickersDayProps) => {
    const { day, outsideCurrentMonth, ...other } = props;

    if (!selectedDate) {
      return <PickersDay day={day} outsideCurrentMonth={outsideCurrentMonth} {...other} />;
    }

    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const end = endOfWeek(selectedDate, { weekStartsOn: 1 });

    const dayIsBetween = isWithinInterval(day, { start, end });
    const isFirstDay = isSameDay(day, start);
    const isLastDay = isSameDay(day, end);

    const wrapperClassName = clsx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = clsx(classes.day, {
      [classes.nonCurrentMonthDay]: outsideCurrentMonth,
      [classes.highlightNonCurrentMonthDay]: outsideCurrentMonth && dayIsBetween,
    });

    return (
      <div className={wrapperClassName}>
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          className={dayClassName}
        />
      </div>
    );
  };

  React.useEffect(() => {
    setDefaultOptions({
      locale: enUS,
      weekStartsOn: 1,
    });
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={selectedDate}
        onChange={handleWeekChange}
        minDate={startOfWeek(twoWeeksAgo)}
        slots={{
          day: renderWeekPickerDay,
        }}
        slotProps={{
          textField: {
            helperText: t('dates.twoWeeksAgoMax'),
          },
        }}
        format={selectedDate ? formatWeekSelectLabel(selectedDate) : 'dd/MM/yyyy'}
      />
    </LocalizationProvider>
  );
};

export default WeekPicker;
